import React, {useEffect, useState, useCallback} from "react";
import {useSelector} from "react-redux";
import Swal from "sweetalert2";
import TicketsService from "../api/tickets";
import {getUrlVars} from "../utils/systemInfo";
import PropTypes from "prop-types";
import {Box, Button, CircularProgress, Typography, Avatar, Divider, IconButton, Stack} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import {useTheme, styled} from "@mui/material/styles";
import Breadcrumbs from "../common/breadcrumbs";
import {UserPermissionConstants} from "../utils/permissions";
import utils from "../utils/utils";
import {StatusChip} from "../common/statusChips";
import CustomTextField from "../common/CustomTextField";
import {useNavigate} from "react-router-dom";
import handleNavigate from "../common/customFunctions/handleNavigate";
import {useTranslation} from "react-i18next";

const StyledButton = styled((props) => <Button {...props} />)(({theme}) => ({
    "backgroundColor": theme.palette.custom.buttonColor,
    "color": theme.palette.custom.buttonTextColor,
    "&:hover": {
        backgroundColor: theme.palette.custom.buttonHoverColor,
        color: theme.palette.custom.buttonHoverTextColor,
        boxShadow: "none"
    }
}));

function TicketView() {
    const organization = useSelector(({organziation}) => organziation);
    const ticketHeaderUid = getUrlVars()["ticketId"];
    const [closeLoading, setCloseLoading] = useState(false);
    const currentUser = JSON.parse(localStorage.getItem("user"));
    const [ticket, setTicket] = useState(null);
    const [ticketLoading, setTicketLoading] = useState(true);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState("");
    const [posting, setPosting] = useState(false);
    const {t} = useTranslation();
    const permissions = useSelector(({permissions}) => permissions);
    const navigate = useNavigate();
    const theme = useTheme();
    const apiKey = organization?.api_key_list?.[0]?.key ?? localStorage.getItem("apiKey");
    //   const orgUid = organization?.uid;
    const breadcrumbs = [
        {id: 0, name: "Home", href: "./"},
        {id: 1, name: "Ticket Dashboard", href: "indexHelpdesk"},
        {id: 2, name: ticket?.template?.name || "Ticket Details", href: `ticketView?ticketId=${ticketHeaderUid}`}
    ];

    useEffect(() => {
        if (ticket && ticket.status === "new" && !utils.hasPermission(UserPermissionConstants.VIEW_DEVELOPER_PORTAL, permissions)) {
            TicketsService.SaveTicketAPI(
                {
                    template_header_uid: "",
                    name: "",
                    description: "",
                    order_by: "",
                    status: "open",
                    ticket_status: "open",
                    updated_by_admin_id: "",
                    requirements: "",
                    ticket_header_uid: ticket.ticket_header_uid,
                    app_profile_uid: "",
                    operation_profile_uid: "",
                    ticket_template_header_uid: "",
                    created_by_person_uid: ""
                },
                apiKey
            );
        }
    }, [ticket, currentUser, apiKey, permissions]);

    useEffect(() => {
        setLoading(true);
        TicketsService.GetTicketView({ticket_header_uid: ticketHeaderUid, apiKey})
            .then((ticketRes) => {
                console.log(ticketRes);
                const foundTicket = (ticketRes.ticket || []).find((t) => t.ticket_header_uid.toString() === ticketHeaderUid.toString()) || null;
                setTicket(foundTicket);
            })
            .catch(() => setTicket(null))
            .finally(() => setTicketLoading(false));

        TicketsService.GetTicketComments(ticketHeaderUid, apiKey)
            .then((commentRes) => {
                setComments(commentRes.ticket_feedback || []);
            })
            .catch(() => setComments([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketHeaderUid, apiKey]);

    const handlePostComment = async () => {
        if (!commentText.trim()) {
            Swal.fire({icon: "info", title: "Cannot submit an empty comment!"});
            return;
        }
        const result = await Swal.fire({
            title: "Add Comment?",
            showDenyButton: true,
            confirmButtonText: "Add",
            denyButtonText: "Don't Add",
            background: theme.palette.custom.inputBackgroundColor,
            color: theme.palette.custom.text
        });
        if (!result.isConfirmed) return;
        setPosting(true);
        await TicketsService.PostTicketComment({
            ticket_header_uid: ticketHeaderUid,
            description: commentText,
            updated_by_person_uid: currentUser.profileUid,
            status: "active",
            apiKey
        });
        setCommentText("");
        const commentRes = await TicketsService.GetTicketComments(ticketHeaderUid, apiKey);
        setComments(commentRes.ticket_feedback || []);
        setPosting(false);
    };

    const handleDeleteComment = useCallback(
        async (commentUid) => {
            const result = await Swal.fire({
                title: "Delete Comment?",
                showDenyButton: true,
                confirmButtonText: "Delete",
                denyButtonText: "Don't Delete",
                background: theme.palette.custom.inputBackgroundColor,
                color: theme.palette.custom.text
            });
            if (!result.isConfirmed) return;
            await TicketsService.DeleteTicketComment({
                ticket_feedback_uid: commentUid,
                ticket_header_uid: ticketHeaderUid,
                updated_by_person_uid: currentUser.profileUid,
                status: "removed",
                apiKey
            });
            const commentRes = await TicketsService.GetTicketComments(ticketHeaderUid, apiKey);
            setComments(commentRes.ticket_feedback || []);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ticketHeaderUid, apiKey, currentUser]
    );

    const handleRefreshComments = useCallback(async () => {
        setLoading(true);
        const commentRes = await TicketsService.GetTicketComments(ticketHeaderUid, apiKey);
        setComments(commentRes.ticket_feedback || []);
        setLoading(false);
    }, [ticketHeaderUid, apiKey]);

    const handleCloseTicket = async () => {
        const result = await Swal.fire({
            title: "Are you sure you want to close this ticket?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Close Ticket",
            cancelButtonText: "Cancel",
            background: theme.palette.custom.inputBackgroundColor,
            color: theme.palette.custom.text
        });
        if (!result.isConfirmed) return;
        setCloseLoading(true);
        try {
            const apiKey = localStorage.getItem("apiKey");
            await TicketsService.SaveTicketAPI(
                {
                    template_header_uid: "",
                    name: "",
                    description: "",
                    order_by: "",
                    status: "closed",
                    ticket_status: "closed",
                    updated_by_admin_id: "",
                    requirements: "",
                    ticket_header_uid: ticket.ticket_header_uid,
                    app_profile_uid: "",
                    operation_profile_uid: "",
                    ticket_template_header_uid: "",
                    created_by_person_uid: ""
                },
                apiKey
            );
            Swal.fire({icon: "success", title: "Ticket closed!"});
            handleNavigate(event, navigate, "/myTickets");
        } catch (e) {
            Swal.fire({
                icon: "error",
                title: "Failed to close ticket. Please try again."
            });
        } finally {
            setCloseLoading(false);
        }
    };

    if (ticketLoading)
        return (
            <Box sx={{textAlign: "center", pt: 4}}>
                <CircularProgress />
            </Box>
        );
    if (!ticket)
        return (
            <Box sx={{textAlign: "center", pt: 4}}>
                <Typography variant="h6">Ticket not found.</Typography>
            </Box>
        );

    return (
        <Box
            sx={{
                p: 2,
                minHeight: "90vh",
                height: "fit-content",
                overflow: "auto",
                mx: "auto"
            }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {xs: "column", sm: "row"},
                    justifyContent: "space-between",
                    alignItems: {xs: "flex-start", sm: "center"},
                    mb: 2
                }}>
                <Box sx={{flex: 1, mb: {xs: 1, sm: 0}}}>
                    <Breadcrumbs breadcrumbs={breadcrumbs} />
                    <Typography
                        sx={{
                            fontSize: "45px",
                            mb: 0,
                            color: theme.palette.custom.text
                        }}
                        variant="h2"
                        gutterBottom>
                        {ticket.template?.name || "Ticket Details"}
                    </Typography>
                </Box>
            </Box>
            <Box variant="outlined" sx={{mb: 3}}>
                <Box>
                    <Box
                        sx={{
                            "display": "flex",
                            "flexWrap": "wrap",
                            "gap": 3,
                            "mb": 2,
                            "& > *": {minWidth: "200px", flex: "1 1 220px"}
                        }}>
                        <Box>
                            <Typography variant="subtitle2">Ticket Ref:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {ticket.ticket_number}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">Status:</Typography>
                            <StatusChip status={ticket.status} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">Assigned To:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {ticket.assigned_to_person?.firstname} {ticket.assigned_to_person?.lastname}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">Created By:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {ticket.created_by_person?.firstname} {ticket.created_by_person?.lastname}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">Updated By:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {ticket.updated_by_person?.firstname} {ticket.updated_by_person?.lastname}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">Created:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {ticket.created_timestamp}
                            </Typography>
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2">Description:</Typography>
                        <Typography variant="body2">{ticket.template?.description}</Typography>
                    </Box>
                </Box>
            </Box>
            <Box variant="outlined" sx={{mb: 3}}>
                <Box>
                    <Typography variant="h6" fontWeight={600} mb={1}>
                        Requirements
                    </Typography>
                    <Divider sx={{mb: 1}} />
                    {ticket.template?.requirements?.length ? (
                        ticket.template.requirements.map(
                            (req) =>
                                req.status === "active" && (
                                    <Box key={req.name} mb={1.5}>
                                        <Typography fontWeight={600}>{req.name}</Typography>
                                        <Stack pl={2} spacing={0.5}>
                                            {req.inputs?.map(
                                                (input) =>
                                                    input.status === "active" && (
                                                        <Box key={input.name}>
                                                            <Typography component="span" fontWeight={500} mr={0.5}>
                                                                {input.name}:
                                                            </Typography>
                                                            {input.input_requirement_config_uid === 6 ? (
                                                                (() => {
                                                                    const checkedUids = input.values?.[0]?.value || [];
                                                                    const checkedNames = input.select
                                                                        .filter((option) => option.status === "active" && checkedUids.includes(Number(option.template_requirement_input_select_uid)))
                                                                        .map((option) => option.name)
                                                                        .join(", ");
                                                                    return <Typography component="span">{checkedNames || <em>None</em>}</Typography>;
                                                                })()
                                                            ) : (
                                                                <Typography component="span">{(input.values || []).map((v) => v.value).join(", ")}</Typography>
                                                            )}
                                                        </Box>
                                                    )
                                            )}
                                        </Stack>
                                    </Box>
                                )
                        )
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No requirements.
                        </Typography>
                    )}
                </Box>
            </Box>
            <Box variant="outlined">
                <Box>
                    <CommentList comments={comments} onRefresh={handleRefreshComments} onDelete={handleDeleteComment} currentUser={currentUser} loading={loading} />
                    <Divider sx={{my: 2}} />
                    <Box>
                        <CustomTextField variant="filled" label="Write a comment" multiline minRows={3} fullWidth value={commentText} onChange={(e) => setCommentText(e.target.value)} sx={{mb: 1}} />
                        <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                            <StyledButton
                                variant="contained"
                                sx={{
                                    "backgroundColor": "red",
                                    "color": theme.palette.custom.buttonTextColor,
                                    "&:hover": {
                                        backgroundColor: theme.palette.custom.buttonHoverColor,
                                        color: theme.palette.custom.buttonHoverTextColor
                                    },
                                    "marginTop": "1rem",
                                    "marginRight": "10px"
                                }}
                                disabled={ticket.status === "closed" || closeLoading}
                                onClick={handleCloseTicket}>
                                {t("Close Ticket")}
                            </StyledButton>
                            <StyledButton
                                variant="contained"
                                sx={{
                                    "backgroundColor": theme.palette.custom.buttonColor,
                                    "color": theme.palette.custom.buttonTextColor,
                                    "&:hover": {
                                        backgroundColor: theme.palette.custom.buttonHoverColor,
                                        color: theme.palette.custom.buttonHoverTextColor
                                    },
                                    "marginTop": "1rem"
                                }}
                                disabled={posting || !commentText.trim() || ticket.status === "closed" || closeLoading}
                                onClick={handlePostComment}>
                                {t("Post Comment")}
                            </StyledButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default TicketView;

function CommentList({comments, onRefresh, onDelete, currentUser, loading}) {
    const theme = useTheme();
    let lastDate = "";
    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={700}>
                    Comments
                </Typography>
                    {loading && <CircularProgress size={20} sx={{ml: 1}} />}
                    <IconButton onClick={onRefresh} disabled={loading} sx={{float: "right"}}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            {comments.length === 0 && (
                <Typography variant="body2" color={theme.palette.custom.text}>
                    No comments yet.
                </Typography>
            )}
            {comments.map((c, i) => {
                const updatedDate = c.updated_timestamp?.slice(0, 10);
                const updatedTime = c.updated_timestamp?.slice(11, 16);
                const isMine = `${c.updated_by_person?.firstname || ""} ${c.updated_by_person?.lastname || ""}`.trim() === `${currentUser.firstName} ${currentUser.lastName}`.trim();

                const showDate = lastDate !== updatedDate;
                lastDate = updatedDate;

                return (
                    <Box key={c.ticket_feedback_uid || i} mb={2}>
                        {showDate && updatedDate && (
                            <Typography variant="subtitle2" color="primary" sx={{mt: 2, mb: 1, fontWeight: 700}}>
                                {updatedDate}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: theme.palette.custom.borderColor,
                                borderRadius: 2,
                                bgcolor: theme.palette.custom.backgroundColor2,
                                p: 2,
                                maxWidth: "600px",
                                ml: isMine ? 0 : "auto",
                                mr: isMine ? "auto" : 0,
                                display: "flex",
                                flexDirection: "column"
                            }}>
                            <Box display="flex" alignItems="center">
                                <Avatar src={c.userPic || c.updated_by_person?.image?.[0]?.public_url || "/assets/images/users/FakeUser.png"} sx={{width: 32, height: 32, mr: 1}} />
                                <Typography fontWeight={700} mr={1}>
                                    {c.updated_by_person?.firstname} {c.updated_by_person?.lastname}
                                </Typography>
                                <Typography ml="auto" variant="caption">
                                    {updatedTime}
                                </Typography>
                                {isMine && (
                                    <IconButton size="small" sx={{ml: 1}} onClick={() => onDelete(c.ticket_feedback_uid)} title="Delete Comment">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography mt={1}>{c.description}</Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

CommentList.propTypes = {
    comments: PropTypes.array,
    onRefresh: PropTypes.func,
    onDelete: PropTypes.func,
    currentUser: PropTypes.object,
    loading: PropTypes.bool
};
